import moment from "moment";
import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import { preventDuplicateRequest } from "../utils/requestManager";

const Picklist = process.env.REACT_APP_PICKLIST_URL || "";
if (!Picklist) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_PICKLIST_URL in your environment variables."
  );
}

const Case = process.env.REACT_APP_CASE_URL || "";
if (!Case) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables."
  );
}

const picklistAPI = api(Picklist);
const caseAPI = api(Case);

// Types
export interface UpdateMedicalAssistantRequest {
  subjectQmeScheduleIds: string;
  medicalAssistantId: number;
  notes: string;
}

export interface UpdateMedicalAssistantResponse {
  isSuccess: boolean;
  message: string;
  result?: any; // Adjust based on API response
}

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

//Fetches the list of Picklist by group name for the dropdowns.
const fetchPickListByGroupName = async (params: any): Promise<any> => {
  try {
    const response = await picklistAPI.post(
      "/api/picklist/items/bygroupname",
      params
    );
    // Map the result to transform pickListItemId into value and displayName into label
    const list = response.data?.result.map((item: any) => ({
      value: item.pickListItemId,
      label: item.displayName,
    }));
    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const fetchVendorList = async (): Promise<any> => {
  try {
    const response = await picklistAPI.get(
      "/api/stakeholder/interpreter/vendors"
    );
    const list = response.data?.result.map((item: any) => ({
      value: item.stakeholderId,
      label: item.entityName,
    }));
    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const fetcQMEDoctorList = async (Id: any): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/case/information/clientcontacts?caseInfoId=${Id}`
    );
    const list = response.data?.result.map((item: any) => ({
      ...item,
      value: item.stakeholderContactId,
      label: item.contactFullName,
    }));

    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const fetcQMEDoctorHospitalList = async (contactID: any): Promise<any> => {
  try {
    const response = await picklistAPI.get(
      `/api/stakeholder/contact/medicarelist?stakeholderContactId=${contactID}`
    );
    
    const list = response.data?.result
      ?.filter((user: any) => user?.providerName)
      .map((item: any) => ({
        ...item,
        value: item.contactMedicareListId,
        label: item.providerInfo,
      }));

    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const addQMEScheduleDetails = async (
  userData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.post<ApiResponse<any>>(
      "/api/appointment/add",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new data:", error);
    throw new Error(
      "Failed to add the data. Please check the data and try again."
    );
  }
};

const updateQMEScheduleDetails = async (
  userData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.put<ApiResponse<any>>(
      "/api/appointment/update",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error update data:", error);
    throw new Error(
      "Failed to update data. Please check the data and try again."
    );
  }
};

const getQMEScheduleDetails = async (ID: any): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/appointment/details?qmeScheduleId=${ID}`
    );
    const data = {
      ...response.data.result,
      dateOfAppointment: moment(response.data.result.dateOfAppointment).format(
        "MM/DD/YYYY"
      ),
    };

    return data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw new Error(
      "Unable to fetch the data. Please check the data and try again."
    );
  }
};

const validateDoctorAppointment = async (
  userData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.post<ApiResponse<any>>(
      "/api/appointment/validate/doctor-appointment",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new data:", error);
    throw new Error(
      "Failed to add the data. Please check the data and try again."
    );
  }
};

const fetchCancelAppointment = async (ID: any): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.post<ApiResponse<any>>(
      `/api/appointment/cancel?subjectQmeScheduleId=${ID}`
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error fetching data:", error);
    throw new Error(
      "Unable to fetch the data. Please check the data and try again."
    );
  }
};
const fetchCancelAppointmentreason = async (
  subjectQmeScheduleIds: string[],
  cancellationReason: string
): Promise<ApiResponse<any>> => {
  try {
    const payload = {
      subjectQmeScheduleIds: subjectQmeScheduleIds.join(","), // Convert array to comma-separated string
      cancellationReason: cancellationReason,
    };

    const response = await caseAPI.post<ApiResponse<any>>(
      "/api/appointment/cancel",
      payload
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error canceling appointment:", error);
    throw new Error("Unable to cancel the appointment. Please try again.");
  }
};

const AddedQMEDoctorList = async (): Promise<any> => {
  try {
    const response = await picklistAPI.get(`/api/common/qmedoctors/list`);
    const list = response.data?.result.map((item: any) => ({
      value: item.id,
      label: item.contactStakeholderName,
    }));
    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const AddedQMEDoctorHospitalList = async (): Promise<any> => {
  try {
    const response = await picklistAPI.get(
      `/api/common/medicare-providers/list`
    );
    const list = response.data?.result.map((item: any) => ({
      value: item.id,
      label: item.providerName,
    }));
    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};
const validateAppointmentByScheduleId = async (
  scheduleId: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.get<ApiResponse<any>>(
      `/api/doctorschedule/validate-appointment/by-scheduleid`,
      {
        params: {
          scheduleId,
        },
      }
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error validating appointment by schedule ID:", error);
    throw new Error(
      "Unable to validate the schedule. Please check the data and try again."
    );
  }
};

const fetchInterpreterListDetails = async (searchText: string, interpreterLanguage: string) => {
  try {
    const response = await caseAPI.get(
      `/api/appointment/interpreter/list?searchText=${searchText}&interpreterLanguage=${interpreterLanguage}`
    );
    return response.data ?? [];
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchInjuryListDetails = async (caseInfoId: number) => {
  try {
    const response = await caseAPI.get(
      `/api/case/subject/injury/list?caseInfoId=${caseInfoId}`
    );
    const result = response.data?.result ?? [];
    const options = result.map((item: any) => ({
      value: item.caseSubjectInjuryID ?? "",
      label: `${item.injuryTypeDesc ?? ""}, ${item.claimNumber ?? ""}, [${
        item.dateOfIncident?.replace(" to ", " - ") ?? ""
      }], ${item.insuranceCarrierName ?? ""}`,
      claimNo: item.claimNumber ?? "",
      dateOfIncident: item.dateOfIncident?.replace(" to ", " - ") ?? "",
      insuranceCarrier: item.insuranceCarrierName ?? "",
      isPrimaryInjury: item.isPrimaryInjury,
      injuryType: item.injuryTypeDesc,
    }));
    return options;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const updateMedicalAssistant = async (
  data: UpdateMedicalAssistantRequest
): Promise<ApiResponse<UpdateMedicalAssistantResponse>> => {
  try {
    const response = await caseAPI.post<
      ApiResponse<UpdateMedicalAssistantResponse>
    >("/api/appointment/medical/assistant/update", data);

    return response.data;
  } catch (error) {
    throw new Error(
      "Unable to update medical assistant. Please try again later."
    );
  }
};

const fetchQMEDoctorContacts = async (
  teamMemberUserId: number,
): Promise<any[]> => {
  const requestKey = `fetchQMEDoctorContacts_${teamMemberUserId}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/doctorschedule/get-team/qmedoctor-contact?teammemberuserid=${teamMemberUserId}`,
      );

      const list = response.data?.result.map((item: any) => ({
        ...item,
        value: item.id,
        label: `${item.contactFullName} - ${item.partyName}`,
        party: item.partyName,
      }));

      return list;
    } catch (error) {
      console.error("Error fetching QME Doctor Contacts:", error);
      throw new Error(
        "Unable to fetch QME Doctor Contacts. Please try again later.",
      );
    }
  });
};

const getAppointmentListByCase = async (caseInfoId: number): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/appointment/list/by-case?caseInfoId=${caseInfoId}`
    );
    const result = response.data?.result;
    if (!result) return [];
    const list = Array.isArray(result)
      ? result.map((item: any) => ({
          value: String(item.subjectQmeScheduleId),
          label: item.appointmentInfo,
        }))
      : [
          {
            value: String(result.subjectQmeScheduleId),
            label: result.appointmentInfo,
          },
        ];

    return list;
  } catch (error) {
    console.error("Error fetching list details:", error);
    throw new Error(
      "Unable to fetch appointment list details. Please try again later."
    );
  }
};

const getAppointmentListByDocumentTypeCase = async (caseInfoId: number, docTypeId: number): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/appointment/list/by-doctype-case?caseInfoId=${caseInfoId}&docTypeId=${docTypeId}`
    );
    const result = response.data?.result;
    if (!result) return [];
    const list = Array.isArray(result)
      ? result.map((item: any) => ({
          value: String(item.subjectQmeScheduleId),
          label: item.appointmentInfo,
        }))
      : [
          {
            value: String(result.subjectQmeScheduleId),
            label: result.appointmentInfo,
          },
        ];

    return list;
  } catch (error) {
    console.error("Error fetching list details:", error);
    throw new Error(
      "Unable to fetch appointment list details. Please try again later."
    );
  }
};

const getQMEProcessStatus = async (subjectQMEScheduleId: any): Promise<any> => {
  try {
    const response = await caseAPI.get<ApiResponse<any>>(
      `/api/appointment/qme-process/status?qmeScheduleId=${subjectQMEScheduleId}`
    );
    const updatedResult = {
      ...response.data.result,
    };
    console.log(updatedResult);

    return updatedResult;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again."
    );
  }
};

export {
  fetchPickListByGroupName,
  fetchVendorList,
  fetcQMEDoctorList,
  fetcQMEDoctorHospitalList,
  addQMEScheduleDetails,
  updateQMEScheduleDetails,
  getQMEScheduleDetails,
  validateDoctorAppointment,
  fetchCancelAppointment,
  AddedQMEDoctorList,
  AddedQMEDoctorHospitalList,
  validateAppointmentByScheduleId,
  fetchCancelAppointmentreason,
  fetchInterpreterListDetails,
  fetchInjuryListDetails,
  updateMedicalAssistant,
  fetchQMEDoctorContacts,
  getAppointmentListByCase,
  getAppointmentListByDocumentTypeCase,
  getQMEProcessStatus,
};
